import React, { forwardRef, useContext, useState } from 'react';
import {
  AppContext,
  UpgradePlanButton,
  BuyAddonButton,
  StartTrialButton,
  featureIdentifyObj,
  useActivityTracker,
  getTranslatedText,
  FeatureGuide,
  FEATURE_GATING_STATUS
} from '@nile/components';
import styled from 'styled-components';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Heading,
  CarouselSlider,
  DisplayText,
  TextStyle,
  Button
} from '@superops/ui-design-system';
import envConfig from '@nile/home/src/Config/env';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { SVGIcon } from '@superops/super-icons';
import { Tooltip } from 'react-clone-tippy';

function useCheckFeatureGating(feature) {
  const appContext = useContext(AppContext);
  const allowedFeatures = appContext.planFeatureList;
  const featureProps = allowedFeatures?.[feature];
  return featureProps;
}

export function isFeatureAvailable(
  allowedFeaturesList = [],
  featureToBeChecked
) {
  const featureProps = allowedFeaturesList?.[featureToBeChecked];

  return Boolean(featureProps?.enabled);
}

/**
 * This HOC is written to Wrap the component with Feature gating check
 * @param {ReactComponent} Component
 */
const resourceMap = {
  ALL_TIME_SHEET: {
    template: 'default',
    headerTitle: true,
    imageMap: {
      organizeAndPlan: 'OrganizeAndPlan',
      manageAndOptimize: 'ManageAndOptimize',
      strategizeAndProfit: 'StrategizeAndProfit'
    },
    stepMap: ['organizeAndPlan', 'manageAndOptimize', 'strategizeAndProfit']
  },
  'network-monitoring-system': {
    template: 'default',
    imageMap: {
      fetchPrinterMetricsInRealTime: 'FetchPrinterMetricsInRealTime',
      manageAllDevicesInOnePlace: 'ManageAllDevicesInOnePlace',
      fetchRouterInfo: 'FetchRouterInfo',
      customOIDs: 'CustomOIDs',
      getAlerts: 'GetAlerts'
    },
    subDescription: true,
    chat: {
      enabled: true,
      message: 'snmp.network.monitor.request.demo'
    },
    imgVideo: {
      img: 'network-monitoring',
      featureName: 'networkMonitoringProbe'
    }
  },
  calendar_scheduling: {
    headerTitle: true,
    template: 'default',
    imageMap: {
      myCalendarView: 'MyCalendarView',
      dispatcherView: 'DispatcherCalendarView'
    },
    stepMap: ['myCalendarView', 'dispatcherView']
  },
  ADVANCED_HIERARCHY_POLICY: {
    headerTitle: true,
    template: 'banner',
    content: {
      title: 'policyMgmt.title.advanceHierarchy',
      description: 'policyMgmt.header.subtext',
      guideLink: 'advancedPolicy',
      bannerImg: 'manageRootPlicySets'
    },
    stepMap: ['organizeAndPlan', 'manageAndOptimize', 'strategizeAndProfit']
  },
  CONTRACT_REPORTS: {
    headerTitle: true,
    template: 'default',
    imageMap: {
      contractReports: 'ContractReports'
    },
    stepMap: ['contractReports']
  },
  RUNBOOK_ACTION_RUN_SCRIPT: {
    template: 'default',
    headerTitle: true,
    imageMap: {
      unifiedRunbooks: 'UnifiedRunbooks'
    },
    stepMap: ['unifiedRunbooks']
  },
  RUNBOOK_ACTION_IT_DOCUMENTATION: {
    template: 'default',
    headerTitle: true,
    imageMap: {
      unifiedRunbooks: 'UnifiedRunbooks'
    },
    stepMap: ['unifiedRunbooks']
  },
  RUNBOOK_ACTION_UPDATE_PROPERTY: {
    template: 'default',
    headerTitle: true,
    imageMap: {
      unifiedRunbooks: 'UnifiedRunbooks'
    },
    stepMap: ['unifiedRunbooks']
  },
  RUNBOOK_ACTION_SIDE_CONVERSATION: {
    template: 'default',
    headerTitle: true,
    imageMap: {
      unifiedRunbooks: 'UnifiedRunbooks'
    },
    stepMap: ['unifiedRunbooks']
  },
  RUNBOOK_ACTION_NOTES: {
    template: 'default',
    headerTitle: true,
    imageMap: {
      unifiedRunbooks: 'UnifiedRunbooks'
    },
    stepMap: ['unifiedRunbooks']
  },
  RUNBOOK_ACTION_WORKLOG: {
    template: 'default',
    headerTitle: true,
    imageMap: {
      unifiedRunbooks: 'UnifiedRunbooks'
    },
    stepMap: ['unifiedRunbooks']
  },
  RUNBOOK_ACTION_AUTO_ACTION: {
    template: 'default',
    headerTitle: true,
    imageMap: {
      unifiedRunbooks: 'UnifiedRunbooks'
    },
    stepMap: ['unifiedRunbooks']
  },
  MDM: {
    template: 'default',
    featureHeader: 'mdm.settings.mdmconfiguration',
    headerTitle: true,
    subDescription: true,
    isTrialEligible: true,
    isDataCleanupRequired: true,
    imageMap: {
      mdm: 'MDM'
    },
    stepMap: ['mdm']
  }
};

const FEATURE_CONFIG = {
  'network-monitoring-system': [
    'NetworkScannedResults',
    'CreateEditCredentialForm',
    'AssetManagementViewList',
    'GatingComponent',
    'CredentialViewListGating',
    'NetworkPropViewList',
    'CreateEditPropForm'
  ],
  WINDOWS_MANAGEMENT: ['ClientSiteInstallerSection', 'AgentUninstallPage'],
  calendar_scheduling: ['SchedulingPage'],
  guardz: ['GuardzSheet', 'ExiumSheet'],
  AI_FEATURES: ['GlobalAI', 'AIComponent', 'AIContainer', 'AIActivator'],
  ai_ticket_summary: [
    'AIComponent',
    'AISummaryContainerFloaterPortal',
    'GlobalAI',
    'AIContainer',
    'AIActivator',
    'TicketNoteAIActionsWrapper'
  ],
  ADVANCED_HIERARCHY_POLICY: ['PolicyGating'],
  CONTRACT_REPORTS: ['CustomReportSheet'],
  RUNBOOK_ACTION_RUN_SCRIPT: ['runbookActionPopover'],
  RUNBOOK_ACTION_IT_DOCUMENTATION: ['runbookActionPopover'],
  RUNBOOK_ACTION_UPDATE_PROPERTY: ['runbookActionPopover'],
  RUNBOOK_ACTION_SIDE_CONVERSATION: ['runbookActionPopover'],
  RUNBOOK_ACTION_NOTES: ['runbookActionPopover'],
  RUNBOOK_ACTION_WORKLOG: ['runbookActionPopover'],
  RUNBOOK_ACTION_AUTO_ACTION: ['runbookActionPopover'],
  MDM: ['MDMConfigurationsPage', 'MDMClientSetupPage']
};

function FeatureGating(props) {
  const { options, innerRef } = props;
  const intl = useIntl();
  if (!options) {
    throw Error('Options is mandatory');
  }
  const { mode: displayMode, feature } = options;
  const mode = displayMode || 'hide';
  const appContext = useContext(AppContext);
  const allowedFeatures = appContext.planFeatureList;
  // const featureProps = useCheckFeatureGating(feature);

  const possibleFeatureNames = [];
  Object.keys(FEATURE_CONFIG).forEach((el) => {
    if (FEATURE_CONFIG[el].includes(props.options.id)) {
      possibleFeatureNames.push(el);
    }
  });
  const featureToCheck = possibleFeatureNames[0];
  const featureProps = useCheckFeatureGating(featureToCheck);
  const flags = useFlags();
  const { user, isSuperopsUser } = appContext;
  const status = featureProps?.status;
  const isTrialEligible = resourceMap[feature]?.isTrialEligible || false;
  const isDataCleanupRequired =
    resourceMap[feature]?.isDataCleanupRequired || false;

  let enabled =
    status === FEATURE_GATING_STATUS.ENABLED ||
    status === FEATURE_GATING_STATUS.IN_TRIAL;

  if (isTrialEligible && status === FEATURE_GATING_STATUS.TRIAL_ELIGIBLE) {
    enabled = true;
  }

  if (
    isDataCleanupRequired &&
    status === FEATURE_GATING_STATUS.EXPIRED &&
    options?.hasDataForCleanup
  ) {
    return <props.component ref={innerRef} {...props} />;
  }

  if (enabled && !featureProps?.upsell) {
    return <props.component ref={innerRef} {...props} />;
  }
  if (!enabled && mode === 'hide') {
    return '';
  }
  const imageMap = resourceMap[feature].imageMap;
  const stepMap = resourceMap[feature].stepMap;

  const corouselSettings = {
    dots: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    speed: 500,
    autoplay: true,
    autoplaySpeed: 3000,
    infinite: true,
    dotsClass: 'slick-dots slick-thumb',
    customPaging: function(i) {
      return (
        <a>
          <div className="carousel-text">
            <FormattedMessage
              id={`app.upgrade.${feature}.${imageMap[stepMap[i]]}`}
            />
          </div>
        </a>
      );
    }
  };
  const clickVideoGuide = (featureName) => {
    useActivityTracker({
      event: `Inproduct purchase - guide - ${feature}`
    });
    window.CommandBar.trackEvent(featureIdentifyObj[featureName]?.id, {});
  };

  if (featureProps?.upsell) {
    switch (resourceMap[feature].template) {
      case 'banner':
        return (
          <Holder className="p20 border-radius-sm">
            <div className="fx g10 actr mb-10">
              <Heading element="h2">
                <FormattedMessage id={resourceMap[feature].content.title} />
              </Heading>
              <div className="mt-5">
                <Tooltip
                  title={getTranslatedText(intl, 'common.upgrade.super')}
                  position="top-start"
                  inertia
                  trigger="mouseenter"
                  animation="shift"
                  animateFill={false}
                >
                  <SVGIcon name="Crown" color="#F0A535" />
                </Tooltip>
              </div>
            </div>
            <div className="mb-10">
              <DisplayText element="p" size="medium" weight="Medium">
                <TextStyle variation="subdued">
                  <FormattedMessage
                    id={resourceMap[feature].content.description}
                  />
                </TextStyle>
              </DisplayText>
            </div>
            <div className="mb-10">
              <FeatureGuide
                featureName={resourceMap[feature].content.guideLink}
                article
                video
              />
            </div>
            <div className="mb-20">
              <img
                className="policy-img-block mt20 mw-100"
                src={`${envConfig.imageRoot}svg/${resourceMap?.[feature].content.bannerImg}.svg`}
                alt="loading"
              />
            </div>
            <UpgradePlanButton secondaryButton feature={feature} />
          </Holder>
        );

      default:
        return (
          <div className="full-height">
            {resourceMap?.[feature]?.featureHeader && (
              <div className="p2040">
                <div className="flex actr g10">
                  <DisplayText element="h1" weight="SemiBold">
                    <FormattedMessage
                      id={resourceMap?.[feature]?.featureHeader}
                    />
                  </DisplayText>
                  <SVGIcon name="Crown" color="#F0A535" />
                </div>
              </div>
            )}
            <Wrapper>
              <Holder>
                <div className="flex col center acenter">
                  {resourceMap?.[feature]?.headerTitle && (
                    <HeaderTitle>
                      <FormattedMessage
                        id={
                          featureProps?.upsellPlanId
                            ? 'app.plan.upgrade.title'
                            : 'app.plan.buyAddon.title'
                        }
                      />
                    </HeaderTitle>
                  )}
                  <Description>
                    <Heading>
                      <FormattedMessage id={`app.plan.upgrade.${feature}`} />
                    </Heading>
                  </Description>
                  {resourceMap?.[feature]?.subDescription && (
                    <Description>
                      <FormattedMessage
                        id={`app.plan.upgrade.desc.${feature}`}
                      />
                    </Description>
                  )}
                  <div className="mt-30 upgrade-button">
                    {featureProps?.upsellAddonId ? (
                      <div className="flex g20">
                        {resourceMap?.[feature]?.chat?.enabled && (
                          <button
                            className="upsellbtn cursor-pointer"
                            onClick={() => {
                              useActivityTracker({
                                event: `Inproduct purchase - chat - ${feature}`
                              });
                              window.Intercom('show');
                              window.Intercom(
                                'showNewMessage',
                                getTranslatedText(
                                  intl,
                                  resourceMap?.[feature]?.chat?.message
                                )
                              );
                            }}
                          >
                            <FormattedMessage id="admin.chatsupport.talk" />
                          </button>
                        )}
                        <BuyAddonButton feature={feature} />
                      </div>
                    ) : (
                      <div className="flex g20">
                        {status === FEATURE_GATING_STATUS.TRIAL_ELIGIBLE && (
                          <StartTrialButton
                            feature={feature}
                            addonId={featureProps?.trialInfo?.addonId}
                            buttonTextKey={`app.plan.start.trial.${feature}`}
                          />
                        )}
                        <UpgradePlanButton feature={feature} />
                      </div>
                    )}
                  </div>
                </div>
                {resourceMap?.[feature]?.imgVideo ? (
                  <div
                    onClick={() => {
                      clickVideoGuide(
                        resourceMap?.[feature].imgVideo.featureName
                      );
                    }}
                  >
                    <img
                      className="fw ptr"
                      src={`${envConfig.imageRoot}svg/${resourceMap?.[feature].imgVideo.img}.svg`}
                    ></img>
                  </div>
                ) : (
                  <CarouselHolder>
                    <CarouselSlider {...corouselSettings}>
                      {resourceMap?.[feature]?.stepMap?.map((item, index) => {
                        return (
                          <div className="carousel-image">
                            <img
                              src={`${envConfig.imageRoot}svg/${
                                imageMap[stepMap[index]]
                              }.svg`}
                            ></img>
                            {/* {index} */}
                          </div>
                        );
                      })}
                    </CarouselSlider>
                  </CarouselHolder>
                )}
              </Holder>
            </Wrapper>
          </div>
        );
    }
  }
  return '';
}

function withFeatureGating(Component, options) {
  // eslint-disable-next-line no-underscore-dangle
  const FeatureGatingWrap = forwardRef((props, ref) => (
    <FeatureGating
      component={Component}
      {...props}
      innerRef={ref}
      options={options}
    />
  ));
  FeatureGatingWrap.displayName = `withFeatureGating-${Component.name ||
    Component.displayName ||
    'Component'}`;
  return FeatureGatingWrap;
}

const Wrapper = styled.div`
  background-image: url(${envConfig.imageRoot}svg/DottedBg.svg);
  height: 100%;
  overflow: auto;
  .Superops-Heading--sizeSecondary {
    font-size: 32px !important;
    line-height: 48px;
    text-align: center;
  }
  .carousel-text {
    width: 200px;
  }
  .slick-dots li {
    width: auto;
    height: 50px !important;
    vertical-align: middle;
  }
  .carousel-image {
  }
`;

const Holder = styled.div`
  background-image: url(${envConfig.imageRoot}svg/Gradient.svg);
  height: 100%;
  background-repeat: no-repeat;
  background-repeat-x: repeat;
`;

const HeaderTitle = styled.div`
  font-size: 20px;
  font-weight: 700;
  line-height: 32px;
  margin-top: 20px;
  color: #f05;
`;
const Description = styled.div`
  margin-top: 10px;
  text-align: center;
  width: 800px;
`;

const CarouselHolder = styled.div`
  padding: 0 60px;
  .carousel-image {
    display: flex !important;
    justify-content: center;
  }
  img {
    width: 75%;
  }
  .slick-dots {
    position: relative;
    margin-bottom: 40px;
  }
  .slick-active .carousel-text {
    color: red;
  }
`;

export default withFeatureGating;

export { useCheckFeatureGating };
